import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { TicketParams } from 'flow/types/Params';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import ContentWrapper from 'flow/route-components/ContentWrapper';
import TicketOverviewNavTitle from './TicketHeader/TicketOverviewNavTitle';
import ConversationTabContent from './ticket-page/tabs/ConversationTabContent';
import TabsTicket, { TicketPageTabKey } from './ticket-page/TabsTicket';
import TicketSideTabContent from './ticket-page/TicketSideTabContent';
import TicketDetailsDrawer, {
    DRAWER_WIDTH,
} from './ticket-page/TicketDetailsDrawer';

// The narrowest main pane the conversation still works in. The feed column
// is 600px wide with 32px of side padding, and the composer footer (tabs,
// actions, send) does not wrap, so 560 keeps it on one line with a small
// margin. The whole ticket row (main pane + panel) is measured, not the main
// pane alone: measuring the pane would make opening the panel shrink it
// below the limit and close the panel again. Below the threshold the panel
// and its toggle are hidden and the conversation gets the whole row.
const MIN_MAIN_PANE_WIDTH = 560;
const DRAWER_WIDTH_THRESHOLD = MIN_MAIN_PANE_WIDTH + DRAWER_WIDTH; // 920
const PREFERS_DRAWER_OPEN_KEY = 'prefersTicketDetailsOpen';
const MAIN_RELATED_SCROLL_ID = 'ticket-main-related-scroll';

const readPrefersDrawerOpen = (): boolean => {
    try {
        return (
            browserSupportStorage() &&
            wrappedLocalStorage.getItem(PREFERS_DRAWER_OPEN_KEY) === 'true'
        );
    } catch {
        return false;
    }
};

const Ticket = () => {
    const { ticketKey } = useParams<TicketParams>();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dockToggleRef = useRef<HTMLButtonElement | null>(null);
    // Only the threshold crossing matters, so the state is the comparison,
    // not the width: a window drag or the panel animation then re-renders the
    // ticket once, not on every pixel. `undefined` until the first measurement.
    const [wideEnough, setWideEnough] = useState<boolean | undefined>(
        undefined,
    );
    const [prefersDrawerOpen, setPrefersDrawerOpen] = useState<boolean>(
        readPrefersDrawerOpen,
    );
    const [selectedTab, setSelectedTab] =
        useState<TicketPageTabKey>('CONVERSATION');
    // Open/close is only animated once the pane has been measured and painted
    // in its settled state — see the `animate` effect below.
    const [animate, setAnimate] = useState(false);

    // Reset to the conversation when switching tickets. A layout effect so
    // the previous ticket's tab is never painted on the next ticket.
    useLayoutEffect(() => {
        setSelectedTab('CONVERSATION');
    }, [ticketKey]);

    useLayoutEffect(() => {
        // useLayoutEffect (not useEffect) to avoid a panel flicker on mount.
        const el = containerRef.current;
        if (!el) return undefined;
        const update = () =>
            setWideEnough(el.offsetWidth >= DRAWER_WIDTH_THRESHOLD);
        const observer = new ResizeObserver(update);
        observer.observe(el);
        update();
        return () => observer.disconnect();
    }, []);

    // Until the pane has been measured its width is unknown — treat that as
    // "not wide enough" rather than assuming wide. Assuming wide meant a saved
    // open preference rendered the panel open on the first frame, so a narrow
    // pane then animated it *closed* on every navigation.
    const measured = wideEnough !== undefined;
    const drawerOpen = prefersDrawerOpen && !!wideEnough;

    // However the panel closes (its close button, or the row narrowing below
    // the threshold), the main pane returns to the conversation: a side tab
    // picked inside the panel must not replace the conversation and composer.
    // The saved preference is kept, so re-widening docks the panel again on
    // its Ticket details fallback. A layout effect so the side tab is never
    // painted in the main pane.
    const drawerWasOpenRef = useRef(drawerOpen);
    useLayoutEffect(() => {
        if (drawerWasOpenRef.current && !drawerOpen) {
            setSelectedTab('CONVERSATION');
        }
        drawerWasOpenRef.current = drawerOpen;
    }, [drawerOpen]);

    // Enable transitions only after a frame has been painted in the measured
    // state, so the initial settle is instant and only later open/close
    // (toggle, or a resize across the threshold) animates.
    useEffect(() => {
        if (!measured) return undefined;
        const frameId = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(frameId);
    }, [measured]);

    const setDrawerOpen = useCallback((value: boolean) => {
        setPrefersDrawerOpen(value);
        if (browserSupportStorage()) {
            wrappedLocalStorage.setItem(
                PREFERS_DRAWER_OPEN_KEY,
                value ? 'true' : 'false',
            );
        }
    }, []);

    const openDrawer = useCallback(() => setDrawerOpen(true), [setDrawerOpen]);

    // Closing the panel always returns the main pane to the conversation.
    const closeDrawer = useCallback(() => {
        setSelectedTab('CONVERSATION');
        setDrawerOpen(false);
    }, [setDrawerOpen]);

    // The conversation occupies the main pane whenever the panel is open
    // (pinned there) or the conversation tab is selected while closed.
    const showConversationInMain = drawerOpen || selectedTab === 'CONVERSATION';

    return (
        <ContentWrapper>
            <div ref={containerRef} className="flex w-full grow">
                <div className="flex w-full grow flex-col overflow-hidden">
                    <div className="h-16 shrink-0">
                        <TicketOverviewNavTitle ticketKey={ticketKey} />
                    </div>
                    {/* Pills live in the main pane while the panel is closed;
                     *  on dock the row collapses (grid-rows 1fr→0fr) and fades
                     *  over 300ms. */}
                    <div
                        className={twMerge(
                            'grid shrink-0',
                            animate
                                ? 'transition-all duration-300 ease-out'
                                : '',
                            drawerOpen
                                ? 'grid-rows-[0fr] opacity-0'
                                : 'grid-rows-[1fr] opacity-100',
                        )}
                        // Collapsed rows stay mounted for the animation; keep
                        // them out of the tab order and accessibility tree.
                        // React 18 passes `inert` through as a plain attribute
                        // (`''` = present); React 19 types it as a boolean, so
                        // this becomes `inert={drawerOpen}` on upgrade.
                        {...((drawerOpen ? { inert: '' } : {}) as Record<
                            string,
                            string
                        >)}
                        aria-hidden={drawerOpen || undefined}
                    >
                        {/* Pill row: in a narrow pane the first pill keeps the
                         *  row's natural 12px ps-3 inset so its left margin
                         *  matches the row's right (pe-3) margin. In a wide pane
                         *  it shifts in to sit under the centered 600px feed
                         *  column (316 = the column's left offset + the 12px
                         *  ps-3); the row still runs full-width to the right so
                         *  the fade + chevrons reach the panel edge. */}
                        <div className="min-h-0 overflow-hidden">
                            <div
                                className="py-3"
                                style={{
                                    paddingInlineStart:
                                        'max(0px, calc(50% - 316px))',
                                }}
                            >
                                <TabsTicket
                                    ticketKey={ticketKey}
                                    selectedTab={selectedTab}
                                    onSelectTab={setSelectedTab}
                                    showDock={!!wideEnough}
                                    onToggleDock={openDrawer}
                                    dockToggleRef={dockToggleRef}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Conversation stays mounted across open/close (just hidden
                     *  when a side tab shows in the main pane) so its scroll
                     *  position is preserved. `visible` tells it whether it is
                     *  on screen, so notifications for this ticket still fire
                     *  while it is hidden. */}
                    <div
                        className={twMerge(
                            'flex min-h-0 flex-1 flex-col',
                            showConversationInMain ? '' : 'hidden',
                        )}
                    >
                        <ConversationTabContent
                            ticketKey={ticketKey}
                            visible={showConversationInMain}
                        />
                    </div>
                    {/* Side-tab content shows in the main pane only when the
                     *  panel is closed and a non-conversation tab is active. */}
                    {!drawerOpen && selectedTab !== 'CONVERSATION' ? (
                        <TicketSideTabContent
                            ticketKey={ticketKey}
                            tab={selectedTab}
                            relatedScrollId={MAIN_RELATED_SCROLL_ID}
                        />
                    ) : null}
                </div>
                {/* Mounted only once the pane has been measured, so the
                 *  Transition starts in its correct state instead of
                 *  transitioning into it (it has no `appear`). From then on it
                 *  stays mounted so the close (leave) animation can play; the
                 *  Transition unmounts the contents once collapsed. */}
                {measured ? (
                    <TicketDetailsDrawer
                        ticketKey={ticketKey}
                        selectedTab={selectedTab}
                        onSelectTab={setSelectedTab}
                        onClose={closeDrawer}
                        open={drawerOpen}
                        returnFocusTo={dockToggleRef}
                    />
                ) : null}
            </div>
        </ContentWrapper>
    );
};

export default React.memo(Ticket);
